/**
 * Fixed-timestep loop (HANDOFF §4.2, mandatory).
 *
 * Accumulator pattern: simulate in fixed steps (1/60 s), render with
 * interpolation. The prototype's variable-dt render-loop mutation made
 * determinism impossible; this decouples sim stepping from rendering.
 *
 * The PURE core — `advanceAccumulator` — contains the determinism-critical math
 * and touches NO DOM / canvas / window, so it is unit-testable in isolation.
 * `createLoop` wraps it in a requestAnimationFrame driver for the live app; only
 * the wall-clock read and rAF scheduling live there (Phase 2 wires rendering).
 */

/** Fixed simulation timestep in seconds (60 Hz). */
export const FIXED_DT = 1 / 60;

/** Max frame time to absorb, in seconds — caps the "spiral of death" on slow frames. */
export const MAX_FRAME_SECONDS = 0.25;

/** Result of folding one real frame into the fixed-step accumulator. */
export interface AccumulatorResult {
  /** Number of fixed sim steps that should run this frame. */
  readonly steps: number;
  /** Leftover accumulated time carried to the next frame (seconds). */
  readonly remainder: number;
  /** Interpolation factor in [0, 1) for rendering between sim states. */
  readonly alpha: number;
}

/**
 * Pure accumulator math (§4.2): given the time already accumulated and the real
 * elapsed time since the last frame, decide how many fixed steps to run and the
 * render interpolation alpha. Deterministic — same inputs, same outputs.
 *
 * @param accumulated  seconds carried over from prior frames (≥ 0)
 * @param frameSeconds real seconds elapsed this frame (clamped to MAX_FRAME_SECONDS)
 * @param fixedDt      fixed timestep (defaults to {@link FIXED_DT})
 */
export function advanceAccumulator(
  accumulated: number,
  frameSeconds: number,
  fixedDt: number = FIXED_DT,
): AccumulatorResult {
  const clamped = Math.min(Math.max(frameSeconds, 0), MAX_FRAME_SECONDS);
  let acc = accumulated + clamped;
  let steps = 0;
  while (acc >= fixedDt) {
    acc -= fixedDt;
    steps++;
  }
  return { steps, remainder: acc, alpha: acc / fixedDt };
}

export interface LoopCallbacks {
  /** Advance the simulation by exactly FIXED_DT seconds. */
  step: (dt: number) => void;
  /** Render the current state; `alpha` is the interpolation factor [0, 1). */
  render: (alpha: number) => void;
}

export interface Loop {
  start(): void;
  stop(): void;
}

/**
 * Create a fixed-timestep loop driven by requestAnimationFrame.
 * Sim stepping is deterministic; only rendering sees wall-clock interpolation.
 */
export function createLoop({ step, render }: LoopCallbacks): Loop {
  let rafId: number | null = null;
  let last = 0;
  let accumulator = 0;

  const frame = (now: number): void => {
    const { steps, remainder, alpha } = advanceAccumulator(accumulator, (now - last) / 1000);
    last = now;
    for (let i = 0; i < steps; i++) step(FIXED_DT);
    accumulator = remainder;
    render(alpha);
    rafId = requestAnimationFrame(frame);
  };

  return {
    start() {
      if (rafId !== null) return;
      last = performance.now();
      accumulator = 0;
      rafId = requestAnimationFrame(frame);
    },
    stop() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    },
  };
}
